import { Box, Text, UnstyledButton } from '@mantine/core';
import { dayIso, daysInMonth, firstWeekdayMon, monthsInRange } from '@/lib/schedule';
import classes from './ScheduleCalendar.module.css';

interface ScheduleCalendarProps {
  /** Выбранные дни, ISO «yyyy-mm-dd». */
  readonly value: readonly string[];
  readonly onChange?: (next: string[]) => void;
  /** Границы выбираемого диапазона (вкл.), ISO. */
  readonly minIso: string;
  readonly maxIso: string;
  /** Максимум выбираемых дней (= requiredDays). */
  readonly max: number;
  /** Только просмотр (без выбора). */
  readonly disabled?: boolean;
  /** Подтверждённый график — выбранные дни зелёным. */
  readonly confirmed?: boolean;
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

// Кастомный мультивыбор-календарь на ISO-строках (без @mantine/dates/Date-таймзон). Месяцы диапазона
// [minIso, maxIso]; дни вне диапазона неактивны; набор ограничен max (= requiredDays).
export default function ScheduleCalendar({ value, onChange, minIso, maxIso, max, disabled, confirmed }: ScheduleCalendarProps) {
  const selected = new Set(value);
  const months = monthsInRange(minIso, maxIso);

  function toggle(iso: string): void {
    if (disabled || !onChange) {
      return;
    }
    if (selected.has(iso)) {
      onChange(value.filter((d) => d !== iso));
    } else if (value.length < max) {
      onChange([...value, iso].sort());
    }
    // При достижении max добор игнорируем — нужно снять лишнюю дату (счётчик это поясняет).
  }

  return (
    <Box className={classes.months}>
      {months.map(({ year, month }) => {
        const total = daysInMonth(year, month);
        const offset = firstWeekdayMon(year, month);
        const cells: Array<{ key: string; day?: number; iso?: string }> = [];
        for (let i = 0; i < offset; i += 1) {
          cells.push({ key: `b${year}-${month}-${i}` });
        }
        for (let day = 1; day <= total; day += 1) {
          const iso = dayIso(year, month, day);
          cells.push({ key: iso, day, iso });
        }

        return (
          <Box key={`${year}-${month}`} className={classes.month}>
            <Text className={classes.title} fz="sm">
              {MONTHS[month - 1]} {year}
            </Text>
            <Box className={classes.grid}>
              {WEEKDAYS.map((wd) => (
                <Box key={wd} className={classes.weekday}>
                  {wd}
                </Box>
              ))}
              {cells.map((cell) => {
                if (cell.day == null || cell.iso == null) {
                  return <Box key={cell.key} className={`${classes.cell} ${classes.blank}`} />;
                }
                const inRange = cell.iso >= minIso && cell.iso <= maxIso;
                const isSelected = selected.has(cell.iso);
                const interactive = !disabled && inRange;

                const cls = [classes.cell];
                if (isSelected) {
                  cls.push(confirmed ? classes.selectedConfirmed : classes.selected);
                } else if (interactive) {
                  cls.push(classes.selectable);
                } else {
                  cls.push(classes.disabled);
                }

                if (interactive) {
                  return (
                    <UnstyledButton
                      key={cell.key}
                      className={cls.join(' ')}
                      onClick={() => toggle(cell.iso!)}
                      aria-pressed={isSelected}
                    >
                      {cell.day}
                    </UnstyledButton>
                  );
                }
                return (
                  <Box key={cell.key} className={cls.join(' ')} aria-disabled>
                    {cell.day}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
